import {
  Box,
  type OverridableStringUnion,
  Skeleton,
  Stack,
  Typography,
  type TypographyPropsVariantOverrides,
  type TypographyVariant,
} from "~/common/mui";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { AppliedFilters } from "~/features/search/components/Filters/components/AppliedFilters/AppliedFilters";
import { SearchFilters } from "~/features/search/components/Filters/SearchFilters";
import { selectVisibleFilters } from "~/features/search/redux/filterConfigSelectors";
import { useSelector } from "react-redux";
import { selectProductResultAmount } from "~/features/search/redux/searchSelectors";
import { isComingFromSeriesTreeClick } from "~/features/search/redux/seriesTreeSelectors";
import { Filter } from "~/features/search/models/filterConfigModels";
import { SearchDisplayMode } from "~/features/search/models/models";
import { SearchPageType } from "~/features/search/models/SearchPageType";
import { useSearchPageType } from "~/features/search/context/SearchPageTypeContext";
import type { RootState } from "~/app/store";
import { useSelectAppliedFilters } from "~/features/search/components/Filters/components/AppliedFilters/useSelectAppliedFilters";
import { FilterSectionSkeleton } from "./components/FilterSectionSkeleton";
import { selectSearchDisplayMode } from "~/domains/userPreferences/redux/selectors";
import { theme } from "~/app/theme";

interface Props {
  titleFontVariant?: OverridableStringUnion<TypographyVariant | "inherit", TypographyPropsVariantOverrides>;
  hideTechFilterButtons?: boolean;
  loading?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  methods?: any;
  onSubmit?: (data: Record<string, { min?: number | undefined; max?: number | undefined }>) => void;
}

export const ReduxFilterSection = memo(function ReduxFilterSection({
  titleFontVariant = "headlineSmall",
  hideTechFilterButtons,
  loading,
  methods,
  onSubmit,
}: Props) {
  const { t } = useTranslation();
  const isTabularDisplay = useSelector(selectSearchDisplayMode) === SearchDisplayMode.TABULAR;
  const isProductFamilySelected = useSelector(selectIsProductFamilySelected);
  const isPlp = useSearchPageType() === SearchPageType.PLP;

  const hideNonCategoryFilters = isTabularDisplay && !isProductFamilySelected && !isPlp;

  let filterConfig = useSelector(selectVisibleFilters);

  const productAmount = useSelector(selectProductResultAmount);
  const isComingFromSeriesTree = useSelector(isComingFromSeriesTreeClick);

  const appliedFilters = useSelectAppliedFilters();

  if (!loading && isComingFromSeriesTree) {
    filterConfig = filterConfig.filter((item) => item.type !== Filter.TAXONOMY);
  }

  if (!loading && !productAmount && appliedFilters.length === 0 && filterConfig.length === 0) {
    return null;
  }

  const title = (
    <Box
      sx={{
        [theme.breakpoints.up("desktop-s")]: {
          height: "48px",
          position: "sticky",
          top: 0,
          zIndex: 2,
          backgroundColor: theme.palette.surface.bright,
        },
      }}
    >
      <Typography
        variant={titleFontVariant}
        sx={{
          fontWeight: "bold",
          [theme.breakpoints.up("desktop-s")]: {
            pt: 2,
          },
        }}
      >
        {loading ? <Skeleton /> : t("page.search.filters.title")}
      </Typography>
    </Box>
  );

  return (
    <Stack spacing={{ phone: 1, "desktop-s": 2 }}>
      {title}
      {loading && <FilterSectionSkeleton amount={5} />}
      {!loading && (
        <>
          <AppliedFilters appliedFilters={appliedFilters} hideClearAll={hideTechFilterButtons} />
          <SearchFilters
            filterConfig={filterConfig}
            hideTechFilterButtons={hideTechFilterButtons}
            hideNonCategoryFilters={hideNonCategoryFilters}
            methods={methods}
            onSubmit={onSubmit}
          />
        </>
      )}
    </Stack>
  );
});

const selectIsProductFamilySelected = (state: RootState) => state.srp.facets.selectedProductGroups.length > 0;
